import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getArticleById } from '@/mocks/articles';
import { getCategoryName } from '@/mocks/categories';
import RelatedArticles from './components/RelatedArticles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const categoryName = article ? getCategoryName(article.categorySlug) : '';

  if (!id || !article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
              <i className="ri-file-unknow-line text-6xl text-foreground-300"></i>
              <h2 className="text-2xl font-bold text-foreground-700 mt-4 font-heading">文章未找到</h2>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 黑料社`}
        description={article.excerpt}
        keywords={`黑料社,${article.category},${article.tags.join(',')}`}
        canonicalUrl={`https://heiliaoshe.com/article/${article.id}`}
        ogType="article"
        articleData={{
          publishedAt: article.publishedAt,
          author: article.author,
          tags: article.tags,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaoshe.com/' },
            { '@type': 'ListItem', position: 2, name: categoryName, item: `https://heiliaoshe.com/category/${article.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `https://heiliaoshe.com/article/${article.id}` },
          ],
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.publishedAt,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: '黑料社' },
          url: `https://heiliaoshe.com/article/${article.id}`,
          keywords: article.tags.join(','),
          inLanguage: 'zh-CN',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 bg-primary-500 text-background-50 text-sm font-bold rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  {article.category}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1 bg-accent-500 text-background-50 text-sm font-bold rounded-full whitespace-nowrap">
                    <i className="ri-fire-fill mr-1"></i>热门
                  </span>
                )}
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight font-heading">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-background-200">
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-user-line text-primary-600 text-sm"></i>
                  </div>
                  <span className="font-medium">{article.author}</span>
                </div>
                <span className="text-sm text-foreground-500">{article.publishedAt}</span>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-message-2-line"></i> {article.comments.toLocaleString()} 评论
                  </span>
                </div>
              </div>
            </div>

            <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="article-content text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-4 mt-10 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-foreground-600"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-foreground-600"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-foreground-600"></i>
              </a>
            </div>

            <RelatedArticles currentId={article.id} categorySlug={article.categorySlug} />
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}